"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  PaginationState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

/**
 * Aggregated table state. Mirrors the slices TanStack Table v8 already
 * understands so the hook output can be plugged straight into
 * `useReactTable({ state })` without further mapping.
 */
export interface TableState {
  /** Per-column visibility flags. Missing entries default to visible. */
  columnVisibility: VisibilityState;
  /** Per-column widths (px). Missing entries fall back to the column's `width`. */
  columnSizes: ColumnSizingState;
  /** Display order of columns by `key`. Empty array means "use natural order". */
  columnOrder: ColumnOrderState;
  /** Sort stack — multi-sort respects array order. */
  sorting: SortingState;
  /** Per-column filter values. */
  columnFilters: ColumnFiltersState;
  /** Pagination cursor. */
  pagination: PaginationState;
  /** Selected row ids. */
  rowSelection: RowSelectionState;
  /** Cross-column quick-filter string. */
  globalFilter: string;
}

/** Initial-state knobs for {@link useTableState}. */
export type TableStateInit = Partial<TableState>;

/** Storage prefix for persisted table state. Exported for tests / migrations. */
export const TABLE_STATE_STORAGE_PREFIX = "aio:table:";

/**
 * Setter API. Each setter accepts the next slice value (no functional
 * updaters here — the table engine pushes whole-slice replacements).
 */
export interface TableStateSetters {
  columnVisibility: (next: VisibilityState) => void;
  columnSizes: (next: ColumnSizingState) => void;
  columnOrder: (next: ColumnOrderState) => void;
  sorting: (next: SortingState) => void;
  columnFilters: (next: ColumnFiltersState) => void;
  pagination: (next: PaginationState) => void;
  rowSelection: (next: RowSelectionState) => void;
  globalFilter: (next: string) => void;
}

/** Return shape of {@link useTableState}. */
export interface UseTableStateResult {
  /** Current aggregated state. */
  state: TableState;
  /** Per-slice setters. */
  set: TableStateSetters;
  /** Reset to the initial state passed at mount. */
  reset: () => void;
}

function defaultState(): TableState {
  return {
    columnVisibility: {},
    columnSizes: {},
    columnOrder: [],
    sorting: [],
    columnFilters: [],
    pagination: { pageIndex: 0, pageSize: 25 },
    rowSelection: {},
    globalFilter: "",
  };
}

function mergeInitial(init: TableStateInit | undefined): TableState {
  const base = defaultState();
  if (!init) return base;
  return {
    columnVisibility: init.columnVisibility ?? base.columnVisibility,
    columnSizes: init.columnSizes ?? base.columnSizes,
    columnOrder: init.columnOrder ?? base.columnOrder,
    sorting: init.sorting ?? base.sorting,
    columnFilters: init.columnFilters ?? base.columnFilters,
    pagination: init.pagination ?? base.pagination,
    rowSelection: init.rowSelection ?? base.rowSelection,
    globalFilter: init.globalFilter ?? base.globalFilter,
  };
}

function readPersisted(persistKey: string | undefined, fallback: TableState): TableState {
  if (!persistKey) return fallback;
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(`${TABLE_STATE_STORAGE_PREFIX}${persistKey}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<TableState>;
    // Merge so missing slices fall back to defaults. We deliberately don't
    // throw on shape drift — old payloads should still hydrate the slices
    // they do define, and unknown fields are ignored.
    return mergeInitial({ ...fallback, ...parsed });
  } catch (err) {
    // Corrupt JSON or storage access denied. Surface a warning so the bug
    // doesn't hide silently, but keep the table working with defaults.
    console.warn(
      `[useTableState] Failed to hydrate state for key "${persistKey}":`,
      err,
    );
    return fallback;
  }
}

/**
 * React state container for a TanStack Table v8 instance.
 *
 * - `initial` seeds the slices once at mount.
 * - When `persistKey` is provided, every state change is written to
 *   `localStorage` under `aio:table:${persistKey}`, and the next mount
 *   hydrates from that key.
 * - With no `persistKey`, the hook is purely in-memory.
 *
 * The setters take the *next* slice value (TanStack's table-managed
 * `OnChangeFn` callbacks pre-resolve any updater function, so consumers
 * never need to cope with a `Updater<T>` here).
 */
export function useTableState(
  initial?: TableStateInit,
  persistKey?: string,
): UseTableStateResult {
  // Capture the initial value so `reset` returns to the *original* seed,
  // not whatever the most recent state happened to be.
  const initialRef = useRef<TableState>(mergeInitial(initial));

  const [state, setState] = useState<TableState>(() =>
    readPersisted(persistKey, initialRef.current),
  );

  // Persist on every state change (when a persistKey is set). Skip the
  // initial render so we don't immediately overwrite what we just read.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (!persistKey) return;
    if (typeof window === "undefined") return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      window.localStorage.setItem(
        `${TABLE_STATE_STORAGE_PREFIX}${persistKey}`,
        JSON.stringify(state),
      );
    } catch (err) {
      console.warn(
        `[useTableState] Failed to persist state for key "${persistKey}":`,
        err,
      );
    }
  }, [persistKey, state]);

  const set: TableStateSetters = {
    columnVisibility: useCallback(
      (next) => setState((prev) => ({ ...prev, columnVisibility: next })),
      [],
    ),
    columnSizes: useCallback(
      (next) => setState((prev) => ({ ...prev, columnSizes: next })),
      [],
    ),
    columnOrder: useCallback(
      (next) => setState((prev) => ({ ...prev, columnOrder: next })),
      [],
    ),
    sorting: useCallback(
      (next) => setState((prev) => ({ ...prev, sorting: next })),
      [],
    ),
    columnFilters: useCallback(
      (next) => setState((prev) => ({ ...prev, columnFilters: next })),
      [],
    ),
    pagination: useCallback(
      (next) => setState((prev) => ({ ...prev, pagination: next })),
      [],
    ),
    rowSelection: useCallback(
      (next) => setState((prev) => ({ ...prev, rowSelection: next })),
      [],
    ),
    globalFilter: useCallback(
      (next) => setState((prev) => ({ ...prev, globalFilter: next })),
      [],
    ),
  };

  const reset = useCallback(() => {
    setState(initialRef.current);
  }, []);

  return { state, set, reset };
}
